import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shield, Award, Clock, Wrench, BadgeCheck, HeartHandshake } from "lucide-react";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function WhyChooseUs() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  // Live overall rating from our Google reviews cache (same source as the marquee)
  const [googleRating, setGoogleRating] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadRating = async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`, { timeout: 15000 });
        if (cancelled) return;
        if (data?.rating != null && !Number.isNaN(Number(data.rating))) {
          setGoogleRating(Number(data.rating));
        }
      } catch (err) {
        console.error("Failed to load Google rating for Why Choose Us:", err);
      }
    };
    loadRating();
    return () => {
      cancelled = true;
    };
  }, []);

  const features = [
    {
      icon: Shield,
      title: t('whyChoose.qualityTitle'),
      description: t('whyChoose.qualityDesc')
    },
    {
      icon: BadgeCheck,
      title: t('whyChoose.transparentTitle'),
      description: t('whyChoose.transparentDesc')
    },
    {
      icon: Clock,
      title: t('whyChoose.serviceTitle'),
      description: t('whyChoose.serviceDesc')
    },
    {
      icon: Wrench,
      title: t('whyChoose.warrantyTitle'),
      description: t('whyChoose.warrantyDesc')
    },
    {
      icon: Award,
      title: t('whyChoose.qualityTitle'),
      description: t('whyChoose.qualityDesc')
    },
    {
      icon: HeartHandshake,
      title: t('whyChoose.serviceTitle'),
      description: t('whyChoose.serviceDesc')
    }
  ];

  const ratingLabel =
    googleRating != null ? googleRating.toFixed(1) : "—";

  return (
    <section className={`py-24 md:py-32 relative overflow-hidden transition-colors duration-300 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`} data-testid="why-choose-section">
      {/* Background accent */}
      <div className={`absolute top-0 right-0 w-1/2 h-1/2 pointer-events-none ${isDark ? 'bg-gradient-to-bl from-blue-900/10 to-transparent' : 'bg-gradient-to-bl from-red-50 to-transparent'}`} />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className={`text-sm uppercase tracking-widest font-medium mb-3 block ${isDark ? 'text-blue-500' : 'text-red-600'}`}>
            Drive Now
          </span>
          <h2 className={`font-unbounded text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('whyChoose.title')} <span className={isDark ? 'text-blue-500' : 'text-red-600'}>{t('whyChoose.titleAccent')}</span>
          </h2>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            {t('whyChoose.description')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`group p-8 transition-all duration-300 border ${
                isDark 
                  ? 'bg-[#121212] border-white/10 hover:border-blue-500/30' 
                  : 'bg-gray-50 border-gray-200 hover:border-red-300 hover:shadow-md'
              }`}
              data-testid={`feature-${index}`}
            >
              <div className={`w-12 h-12 flex items-center justify-center mb-6 transition-colors ${
                isDark 
                  ? 'bg-blue-600/10 group-hover:bg-blue-600/20' 
                  : 'bg-red-600/10 group-hover:bg-red-600/20'
              }`}>
                <feature.icon className={`h-6 w-6 ${isDark ? 'text-blue-500' : 'text-red-600'}`} />
              </div>
              <h3 className={`font-unbounded text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {feature.title}
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className={`mt-16 pt-16 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className={`font-unbounded text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>A+</div>
              <div className={`text-sm uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>BBB Rating</div>
            </div>
            <div>
              <div
                className={`font-unbounded text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
                data-testid="why-choose-google-rating"
              >
                {ratingLabel}
              </div>
              <div className={`text-sm uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {t('whyChoose.googleRating')}
              </div>
            </div>
            <div>
              <div className={`font-unbounded text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>100%</div>
              <div className={`text-xs sm:text-sm uppercase tracking-wide leading-snug max-w-[12rem] mx-auto ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {t('whyChoose.certifiedVehicles')}
              </div>
            </div>
            <div>
              <div className={`font-unbounded text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('whyChoose.customWarranty')}</div>
              <div className={`text-sm uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('whyChoose.dealershipWarranty')}</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
